import { useState } from 'react';

import { FieldErrors, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  SidebarWrapperFooter,
  SidebarWrapperForm,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useDeskId } from '@/hooks/useDeskId';
import useHomeCheckList, { AccountUserPrefsEnum } from '@/hooks/useHomeCheckList';
import { RULE_FORM_ID } from '@/modules/rules/constants';
import { useRuleFormGuards } from '@/modules/rules/hooks/useRuleFormGuards';
import { useRuleMutations } from '@/modules/rules/hooks/useRules';
import { removeFormState } from '@/redux/forms/actions';
import { createYupResolver } from '@/util/forms/createYupResolver';

import { NAME_STEP_FIELDS, createRuleFormSchema, RuleFormValues } from './ruleFormSchema';
import { RuleLogicFields } from './RuleLogicFields';
import { RuleNameFields } from './RuleNameFields';

type RuleFormProps = {
  ruleId?: string;
  defaultValues: RuleFormValues;
  canWrite: boolean;
  onClose: () => void;
};

export const RuleForm = ({ ruleId, defaultValues, canWrite, onClose }: RuleFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deskId = useDeskId();
  const { addRule, saveRule, createStatus, saveStatus } = useRuleMutations(deskId);
  const { markAsComplete } = useHomeCheckList();
  const isCreate = !ruleId;
  const [step, setStep] = useState<'name' | 'logic'>(isCreate ? 'name' : 'logic');

  const form = useForm<RuleFormValues>({
    resolver: createYupResolver<RuleFormValues>(createRuleFormSchema({ deskId, isCreate })),
    defaultValues,
    mode: 'onChange',
  });
  const { isDirty } = useFormState({ control: form.control });

  useRuleFormGuards({ isDirty: canWrite && isDirty, onClose });

  const finish = () => {
    markAsComplete(AccountUserPrefsEnum.UPDATE_RULE);
    dispatch(removeFormState({ formId: RULE_FORM_ID }));
    onClose();
  };

  const onSubmit = ({ name, description, triggers, condition, actions }: RuleFormValues) => {
    if (ruleId) {
      saveRule({ rule_id: ruleId, triggers, condition, actions }, { onSuccess: finish });
      return;
    }
    addRule(
      { name, description, status: 'active', triggers, condition, actions },
      { onSuccess: finish }
    );
  };

  // Done validates the name again, but its error renders on step 1. Go back so the user sees it.
  const onInvalid = (errors: FieldErrors<RuleFormValues>) => {
    if (NAME_STEP_FIELDS.some((field) => errors[field])) {
      setStep('name');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (step === 'name') {
      e.preventDefault();
      if (await form.trigger(NAME_STEP_FIELDS, { shouldFocus: true })) {
        setStep('logic');
      }
      return;
    }
    form.handleSubmit(onSubmit, onInvalid)(e);
  };

  return (
    <Form {...form}>
      <SidebarWrapperForm onSubmit={handleFormSubmit} noValidate className="pr-4">
        {step === 'name' ? (
          <RuleNameFields />
        ) : (
          <RuleLogicFields canWrite={canWrite} isCreate={isCreate} />
        )}

        {canWrite && (isCreate || isDirty) && (
          <SidebarWrapperFooter>
            {step === 'name' && (
              <>
                <Button type="submit">{t('common.continue')}</Button>
                <Button type="button" variant="ghost-neutral" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
              </>
            )}
            {step === 'logic' && isCreate && (
              <>
                <Button type="submit" isLoading={createStatus === 'pending'}>
                  {t('common.done')}
                </Button>
                <Button type="button" variant="ghost-neutral" onClick={() => setStep('name')}>
                  {t('common.previous')}
                </Button>
              </>
            )}
            {step === 'logic' && !isCreate && (
              <>
                <Button type="submit" isLoading={saveStatus === 'pending'}>
                  {t('common.save')}
                </Button>
                <Button type="button" variant="ghost-neutral" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
              </>
            )}
          </SidebarWrapperFooter>
        )}
      </SidebarWrapperForm>
    </Form>
  );
};
